import { useState, useEffect, useRef, useCallback } from "react";
import VideoPlayer from "./VideoPlayer";
import Card from "./Card";
import { DEFAULT_POSTER, posterFallback } from "../constants/media";

type MediaItem = {
  imagePath: string;
  title: string;
  description: string;
  pathToDir: string;
};

type MediaCarouselProps = {
  mediaList: MediaItem[];
};

export default function MediaCarousel({ mediaList }: MediaCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [playerSrc, setPlayerSrc] = useState<string | null>(null);
  const [playerTitle, setPlayerTitle] = useState("");
  const [playerDir, setPlayerDir] = useState("");
  const [playerInitialTime, setPlayerInitialTime] = useState(0);
  const [infoCache, setInfoCache] = useState<Record<string, { description: string; videos: string[] }>>({});
  const [progressCache, setProgressCache] = useState<
    Record<string, { video_src: string; current_time: number; duration: number }>
  >({});
  const [cardDir, setCardDir] = useState("");
  const fetchedDirs = useRef(new Set<string>());
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const advance = useCallback(() => {
    setActiveIndex((i) => (i + 1) % mediaList.length);
  }, [mediaList.length]);

  // Clear caches when media is updated
  useEffect(() => {
    const handler = () => {
      fetchedDirs.current.clear();
      setInfoCache({});
      setProgressCache({});
    };
    window.addEventListener("ossflix-media-updated", handler);
    return () => window.removeEventListener("ossflix-media-updated", handler);
  }, []);

  // Auto-rotate every 8s
  useEffect(() => {
    if (mediaList.length <= 1) return;
    timerRef.current = setInterval(advance, 8000);
    return () => clearInterval(timerRef.current);
  }, [advance, mediaList.length]);

  const goTo = (idx: number) => {
    setActiveIndex(idx);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(advance, 8000);
  };

  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchActiveRef = useRef(false);
  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    touchActiveRef.current = true;
    clearInterval(timerRef.current);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!touchActiveRef.current) return;
    touchActiveRef.current = false;
    const restart = () => {
      timerRef.current = setInterval(advance, 8000);
    };
    if (!start || mediaList.length <= 1) {
      restart();
      return;
    }
    const t = e.changedTouches[0];
    if (!t) {
      restart();
      return;
    }
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Treat as a swipe if horizontal movement dominates and exceeds ~50px.
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2) {
      const dir = dx < 0 ? 1 : -1;
      const len = mediaList.length;
      setActiveIndex((i) => (i + dir + len) % len);
    }
    restart();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchProgressForDir is stable; effect runs when the active slide changes
  useEffect(() => {
    const item = mediaList[activeIndex];
    if (!item || fetchedDirs.current.has(item.pathToDir)) return;
    fetchedDirs.current.add(item.pathToDir);
    fetch(`/api/media/info?dir=${encodeURIComponent(item.pathToDir)}`)
      .then((r) => r.json())
      .then((data) => {
        setInfoCache((prev) => ({
          ...prev,
          [item.pathToDir]: { description: data.description || "", videos: data.videos || [] },
        }));
      })
      .catch(() => {});
    fetchProgressForDir(item.pathToDir);
  }, [activeIndex, mediaList]);

  const fetchProgressForDir = (dirPath: string) => {
    fetch(`/api/playback/progress?dir=${encodeURIComponent(dirPath)}`, { credentials: "same-origin" })
      .then((r) => r.json())
      .then((entries: any[]) => {
        if (entries.length > 0) {
          setProgressCache((prev) => ({ ...prev, [dirPath]: entries[0] }));
        }
      })
      .catch(() => {});
  };

  const handlePlay = (item: MediaItem) => {
    const cached = infoCache[item.pathToDir];
    if (!cached?.videos?.length) return;
    const prog = progressCache[item.pathToDir];
    if (prog && prog.current_time > 0 && (prog.duration === 0 || prog.current_time < prog.duration - 5)) {
      setPlayerSrc(prog.video_src);
      setPlayerInitialTime(prog.current_time);
    } else {
      setPlayerSrc(cached.videos[0]);
      setPlayerInitialTime(0);
    }
    setPlayerTitle(item.title);
    setPlayerDir(item.pathToDir);
  };

  const currentItem = mediaList[activeIndex];
  const currentInfo = currentItem ? infoCache[currentItem.pathToDir] : null;
  const hasProgress = currentItem && progressCache[currentItem.pathToDir]?.current_time > 0;

  return (
    <>
      <div
        className="oss-hero"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {mediaList.map((item, idx) => (
          <div key={item.pathToDir} className={`oss-hero-slide${idx === activeIndex ? " active" : ""}`}>
            {/* Blurred copy fills the stage; the sharp image is either full-bleed (wide banners)
                or a floating poster on the right (portrait art, which looks soft when stretched). */}
            <img className="oss-hero-backdrop" src={item.imagePath || DEFAULT_POSTER} alt="" aria-hidden="true" />
            <img
              className="oss-hero-art"
              src={item.imagePath || DEFAULT_POSTER}
              alt={item.title}
              loading={idx === 0 ? "eager" : "lazy"}
              onError={posterFallback}
              onLoad={(e) => {
                const img = e.currentTarget;
                const portrait = img.naturalWidth > 0 && img.naturalWidth / img.naturalHeight < 1.3;
                img.parentElement?.classList.toggle("oss-hero-slide-poster", portrait);
              }}
            />
          </div>
        ))}
        <div className="oss-hero-vignette" />
        {currentItem && (
          <div className="oss-hero-content">
            <h1 className="oss-hero-title">{currentItem.title}</h1>
            <p className="oss-hero-desc">{currentInfo?.description || ""}</p>
            <div className="oss-hero-actions">
              <button
                type="button"
                className="oss-btn oss-btn-play oss-btn-lg"
                onClick={() => handlePlay(currentItem)}
                disabled={!currentInfo?.videos?.length}
                style={{ opacity: currentInfo?.videos?.length ? 1 : 0.5 }}
              >
                <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5,3 19,12 5,21" />
                </svg>
                {hasProgress ? "Resume" : "Play"}
              </button>
              <button
                type="button"
                className="oss-btn oss-btn-secondary oss-btn-lg"
                onClick={() => setCardDir(currentItem.pathToDir)}
              >
                <svg
                  aria-hidden="true"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                More Info
              </button>
            </div>
          </div>
        )}
        {mediaList.length > 1 && (
          <div className="oss-hero-indicators">
            {mediaList.map((item, idx) => (
              <button
                type="button"
                key={item.pathToDir}
                className={`oss-hero-dot${idx === activeIndex ? " active" : ""}`}
                onClick={() => goTo(idx)}
              />
            ))}
          </div>
        )}
      </div>

      <VideoPlayer
        show={!!playerSrc}
        onHide={() => {
          setPlayerSrc(null);
          if (playerDir) fetchProgressForDir(playerDir);
        }}
        src={playerSrc || ""}
        title={playerTitle}
        dirPath={playerDir}
        initialTime={playerInitialTime}
        onNext={() => {
          const cached = infoCache[playerDir];
          if (!cached?.videos || !playerSrc) return;
          const currentIndex = cached.videos.indexOf(playerSrc);
          if (currentIndex >= 0 && currentIndex < cached.videos.length - 1) {
            setPlayerInitialTime(0);
            setPlayerSrc(cached.videos[currentIndex + 1]);
          } else {
            setPlayerSrc(null);
            if (playerDir) fetchProgressForDir(playerDir);
          }
        }}
        profileId={undefined}
      />

      <Card show={!!cardDir} onHide={() => setCardDir("")} dirPath={cardDir} />
    </>
  );
}
